<script setup lang="ts">
import { reactive } from "vue";
import Container, {
  ContainerType,
  EffectTriggerTiming
} from "../Container.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Container },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    containerType?: ContainerType;
    supportCopyText?: boolean;
    effectTriggerTiming?: EffectTriggerTiming;
    color?: string;
    timeGap?: number;
    lineHeight?: number;
    location?: "horizontal" | "vertical";
    direction?: "toEnd" | "toStart" | "towards" | "fromCenter";
  }>(),
  {
    containerType: "shrink",
    supportCopyText: true,
    effectTriggerTiming: "mouseMove",
    color: "#3cefff",
    timeGap: 50,
    lineHeight: 2,
    location: "horizontal",
    direction: "toEnd"
  }
);

// const emits = defineEmits<{
//   (event: "click"): void;
// }>();

let ratio = 1;
const resize = (controller) => {
  ratio = Math.ceil(
    Math.max(controller.rect.width, controller.rect.height) /
      Math.min(controller.rect.width, controller.rect.height)
  );
};

const commonStyle = reactive<Record<string, string>>({});
const beforeStyle = reactive<Record<string, string>>({});
const afterStyle = reactive<Record<string, string>>({});
switch (props.location) {
  case "horizontal":
    commonStyle.width = "100%";
    commonStyle.height = props.lineHeight + "px";
    beforeStyle.top = "0";
    afterStyle.bottom = "0";
    break;
  case "vertical":
    commonStyle.width = props.lineHeight + "px";
    commonStyle.height = "100%";
    beforeStyle.left = "0";
    afterStyle.right = "0";
    break;
}

const start = () => {
  switch (props.direction) {
    case "toEnd":
      switch (props.location) {
        case "horizontal":
          beforeStyle.right = "0";
          afterStyle.right = "0";
          commonStyle.transformOrigin = "center right";
          commonStyle.transform = "scaleX(0)";
          break;
        case "vertical":
          beforeStyle.bottom = "0";
          afterStyle.bottom = "0";
          commonStyle.transformOrigin = "center bottom";
          commonStyle.transform = "scaleY(0)";
          break;
      }
      break;
    case "toStart":
      switch (props.location) {
        case "horizontal":
          beforeStyle.left = "0";
          afterStyle.left = "0";
          commonStyle.transformOrigin = "center left";
          commonStyle.transform = "scaleX(0)";
          break;
        case "vertical":
          beforeStyle.top = "0";
          afterStyle.top = "0";
          commonStyle.transformOrigin = "center top";
          commonStyle.transform = "scaleY(0)";
          break;
      }
      break;
    case "towards":
      switch (props.location) {
        case "horizontal":
          beforeStyle.right = "0";
          beforeStyle.transformOrigin = "center right";
          afterStyle.left = "0";
          afterStyle.transformOrigin = "center left";
          commonStyle.transform = "scaleX(0)";
          break;
        case "vertical":
          beforeStyle.bottom = "0";
          beforeStyle.transformOrigin = "center bottom";
          afterStyle.top = "0";
          afterStyle.transformOrigin = "center top";
          commonStyle.transform = "scaleY(0)";
          break;
      }
      break;
    case "fromCenter":
      switch (props.location) {
        case "horizontal":
          beforeStyle.left = "50%";
          afterStyle.left = "50%";
          commonStyle.transformOrigin = "center";
          commonStyle.transform = "translateX(-50%) scaleX(0)";
          break;
        case "vertical":
          beforeStyle.top = "50%";
          afterStyle.top = "50%";
          commonStyle.transformOrigin = "center";
          commonStyle.transform = "translateY(-50%) scaleY(0)";
          break;
      }
      break;
  }
};
const end = () => {
  switch (props.direction) {
    case "toEnd":
      switch (props.location) {
        case "horizontal":
          beforeStyle.left = "0";
          afterStyle.left = "0";
          commonStyle.transformOrigin = "center left";
          commonStyle.transform = "scaleX(1)";
          break;
        case "vertical":
          beforeStyle.top = "0";
          afterStyle.top = "0";
          commonStyle.transformOrigin = "center top";
          commonStyle.transform = "scaleY(1)";
          break;
      }
      break;
    case "toStart":
      switch (props.location) {
        case "horizontal":
          beforeStyle.right = "0";
          afterStyle.right = "0";
          commonStyle.transformOrigin = "center right";
          commonStyle.transform = "scaleX(1)";
          break;
        case "vertical":
          beforeStyle.bottom = "0";
          afterStyle.bottom = "0";
          commonStyle.transformOrigin = "center bottom";
          commonStyle.transform = "scaleY(1)";
          break;
      }
      break;
    case "towards":
      switch (props.location) {
        case "horizontal":
          beforeStyle.left = "0";
          beforeStyle.transformOrigin = "center left";
          afterStyle.right = "0";
          afterStyle.transformOrigin = "center right";
          commonStyle.transform = "scaleX(1)";
          break;
        case "vertical":
          beforeStyle.top = "0";
          beforeStyle.transformOrigin = "center top";
          afterStyle.bottom = "0";
          afterStyle.transformOrigin = "center bottom";
          commonStyle.transform = "scaleY(1)";
          break;
      }
      break;
    case "fromCenter":
      switch (props.location) {
        case "horizontal":
          beforeStyle.left = "50%";
          afterStyle.left = "50%";
          commonStyle.transformOrigin = "center";
          commonStyle.transform = "translateX(-50%) scaleX(1)";
          break;
        case "vertical":
          beforeStyle.top = "50%";
          afterStyle.top = "50%";
          commonStyle.transformOrigin = "center";
          commonStyle.transform = "translateY(-50%) scaleY(1)";
          break;
      }
      break;
  }
};

defineExpose({});
</script>

<template>
  <Container
    :type="props.containerType"
    :supportCopyText="props.supportCopyText"
    :effectTriggerTiming="props.effectTriggerTiming"
    @start="start"
    @end="end"
    @ready="resize"
    @update="resize"
  >
    <div
      class="line-double-before"
      :style="{
        backgroundColor: props.color,
        transition: `transform ${props.timeGap * ratio}ms ease-in-out`,
        ...commonStyle,
        ...beforeStyle
      }"
    ></div>
    <div class="line-double">
      <slot></slot>
    </div>
    <div
      class="line-double-after"
      :style="{
        backgroundColor: props.color,
        transition: `transform ${props.timeGap * ratio}ms ease-in-out`,
        ...commonStyle,
        ...afterStyle
      }"
    ></div>
  </Container>
</template>

<style scoped>
.line-double-before,
.line-double-after {
  position: absolute;
  z-index: 0;
}
.line-double {
  position: relative;
  z-index: 1;
  cursor: pointer;
}
</style>
